import {
    CITIES, DIRECTION_BASE_PRICES, DEFAULT_ACTIVE_DIRECTIONS, PENDING_DIRECTIONS,
    FIRST_NAMES, APPLIANCE_BRANDS, PROBLEM_TEXTS
} from './data.js';

const STORAGE_KEY = 'kefir_lk_state_v5';
const SESSION_KEY = 'kefir_lk_session';

function mulberry32(seed) {
    let a = seed;
    return function () {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function pick(rng, arr) {
    return arr[Math.floor(rng() * arr.length)];
}

function pickWeighted(rng, items, weightFn) {
    const total = items.reduce((sum, it) => sum + weightFn(it), 0);
    let r = rng() * total;
    for (const it of items) {
        r -= weightFn(it);
        if (r <= 0) return it;
    }
    return items[items.length - 1];
}

function maskPhone(phone) {
    const m = phone.match(/^\+7 (\d{3}) (\d{3})-(\d{2})-(\d{2})$/);
    if (!m) return phone;
    return `+7 ${m[1].slice(0, 2)}•• ••-${m[3]}-${m[4]}`;
}

// Код "000" не выделен ни одному оператору связи — номера заведомо не существуют в реальности.
function generatePhone(rng) {
    const n = () => Math.floor(rng() * 10);
    const part = (len) => Array.from({ length: len }, n).join('');
    return `+7 000 ${part(3)}-${part(2)}-${part(2)}`;
}

function cityDistrictOrTown(rng, city) {
    const fromRegion = rng() < 0.22 && city.regionTowns.length;
    if (fromRegion) {
        return { label: `${pick(rng, city.regionTowns)} (${city.regionShort})`, isRegion: true, district: null };
    }
    const district = pick(rng, city.districts);
    return { label: district, isRegion: false, district };
}

function priceFor(directionId, city) {
    const base = DIRECTION_BASE_PRICES[directionId] || 500;
    return Math.round((base * city.priceFactor) / 10) * 10;
}

function buildExample(rng, directionId) {
    const brands = APPLIANCE_BRANDS[directionId] || [];
    const problems = PROBLEM_TEXTS[directionId] || ['нужна диагностика'];
    const problem = pick(rng, problems);
    if (brands.length) {
        return `${pick(rng, brands)}, ${problem}`;
    }
    return problem.charAt(0).toUpperCase() + problem.slice(1);
}

function statusForAge(rng, ageHours) {
    if (ageHours < 6) return pick(rng, ['new', 'new', 'progress']);
    if (ageHours < 48) return pick(rng, ['progress', 'called', 'new']);
    const r = rng();
    if (r < 0.45) return 'deal';
    if (r < 0.70) return 'declined';
    if (r < 0.76) return 'declined';
    return pick(rng, ['called', 'progress']);
}

function buildDefaultState() {
    const rng = mulberry32(190485);
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;

    const directions = {};
    DEFAULT_ACTIVE_DIRECTIONS.forEach(id => { directions[id] = { active: true, pending: false }; });
    PENDING_DIRECTIONS.forEach(id => { directions[id] = { active: false, pending: true }; });

    const cities = {};
    CITIES.forEach(city => {
        cities[city.id] = {
            cityOn: !!city.defaultCityOn,
            regionOn: !!city.defaultRegionOn,
            districtsOff: []
        };
    });

    const activeDirectionIds = Object.keys(directions).filter(id => directions[id].active);
    const activeCities = CITIES.filter(c => cities[c.id].cityOn || cities[c.id].regionOn);

    const leads = [];
    let leadNumber = 10420;

    for (let dayOffset = 30; dayOffset >= 0; dayOffset--) {
        const dayDate = new Date(now - dayOffset * DAY);
        const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
        const count = isWeekend
            ? 4 + Math.floor(rng() * 6)
            : 8 + Math.floor(rng() * 11);

        for (let i = 0; i < count; i++) {
            const hour = 8 + Math.floor(rng() * 14);
            const minute = Math.floor(rng() * 60);
            const createdAt = new Date(dayDate);
            createdAt.setHours(hour, minute, 0, 0);
            if (createdAt.getTime() > now) continue;

            const city = pickWeighted(rng, activeCities, c => c.weight);
            const directionId = pick(rng, activeDirectionIds);
            const place = cityDistrictOrTown(rng, city);
            const ageHours = (now - createdAt.getTime()) / 3600000;
            const status = statusForAge(rng, ageHours);
            const cost = priceFor(directionId, city);

            leadNumber++;
            leads.push({
                id: 'lead-' + leadNumber,
                number: leadNumber,
                createdAt: createdAt.getTime(),
                name: pick(rng, FIRST_NAMES),
                phone: generatePhone(rng),
                phoneRevealed: false,
                directionId,
                cityId: city.id,
                place: place.label,
                isRegion: place.isRegion,
                source: rng() < 0.55 ? 'Заявка с сайта' : 'Звонок',
                status,
                cost,
                note: '',
                example: buildExample(rng, directionId),
                dealAmount: status === 'deal' ? Math.round((2500 + rng() * 11000) / 100) * 100 : null,
                dispute: null
            });
        }
    }

    leads.sort((a, b) => b.createdAt - a.createdAt);

    const spent30 = leads.reduce((sum, l) => sum + l.cost, 0);
    const balance = Math.max(12000, 48000 - Math.round(spent30 * 0.08));

    return {
        version: 5,
        balance,
        dailyLimit: 60,
        balanceThreshold: 10000,
        acceptingLeads: true,
        schedule: { mode: 'always', from: '08:00', to: '22:00', roundTheClock: true },
        directions,
        cities,
        leads,
        leadCounter: leadNumber,
        operations: buildInitialOperations(rng, balance),
        notifications: [],
        settings: {
            channels: { telegram: true, email: true, sms: false, browser: false, sound: true },
            crm: { amocrm: 'connected', bitrix24: 'none', retailcrm: 'none', sheets: 'none', webhook: 'none' }
        },
        todayDate: new Date().toDateString(),
        todayCount: 0
    };
}

function buildInitialOperations(rng, balance) {
    const ops = [];
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    let amount = balance;
    for (let i = 0; i < 4; i++) {
        const sum = [20000, 30000, 25000, 40000][i] || 20000;
        ops.push({
            id: 'op-topup-' + i,
            type: 'topup',
            amount: sum,
            date: now - (10 + i * 10) * DAY,
            method: i % 2 === 0 ? 'Картой' : 'Счёт для юрлица'
        });
    }
    return ops;
}

export function loadState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.version === 5) return parsed;
        }
    } catch (e) { /* ignore corrupted storage */ }

    const fresh = buildDefaultState();
    persistState(fresh);
    return fresh;
}

let memoryFallback = null;

export function persistState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
        memoryFallback = state;
    }
}

export function getSession() {
    try {
        const local = localStorage.getItem(SESSION_KEY);
        if (local) return JSON.parse(local);
    } catch (e) { /* ignore */ }
    try {
        const session = sessionStorage.getItem(SESSION_KEY);
        if (session) return JSON.parse(session);
    } catch (e) { /* ignore */ }
    return null;
}

export function setSession(remember) {
    const payload = JSON.stringify({ loggedIn: true, at: Date.now() });
    if (remember) {
        localStorage.setItem(SESSION_KEY, payload);
        sessionStorage.removeItem(SESSION_KEY);
    } else {
        sessionStorage.setItem(SESSION_KEY, payload);
        localStorage.removeItem(SESSION_KEY);
    }
}

export function clearSession() {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
}

export { mulberry32, pick, pickWeighted, generatePhone, cityDistrictOrTown, priceFor, buildExample, maskPhone };
